import Box from '@mui/material/Box';
import { Subscription } from '@rails/actioncable';
import { useQueryClient } from '@tanstack/react-query';
import { Invoice_Read } from '@treadinc/horizon-api-spec';
import { map } from 'lodash-es';
import { useCallback, useContext, useEffect, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Invoice } from '~hooks/useInvoices/models';
import DriverPayPageContext from '~pages/Approvals/DriverPayV2/DriverPayPageContext';
import DriverPayQueryContext from '~pages/Approvals/DriverPayV2/DriverPayQueryContext';
import { makeInvoicesQueryContextKey } from '~pages/Approvals/DriverPayV2/utils';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import useVisibilityAwareLazyLoader from '~utils/hooks/useVisibilityAwareLazyLoader';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import InvoicesTable from '../../InvoicesTable/InvoicesTable';
import InvoicesQueryContext from './InvoicesQueryContext';

export default function InvoicesTab() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const invoicesQuery = useContext(InvoicesQueryContext);
  const { currentCategory } = useContext(DriverPayPageContext);
  const { filters } = useContext(DriverPayQueryContext);
  const lazyLoader = useVisibilityAwareLazyLoader(300);
  const queryClient = useQueryClient();

  const [invoicesRTUSubscription, setInvoicesRTUSubscription] = useState<Subscription>();

  useEffect(() => {
    if (lazyLoader.shouldLoad && !invoicesQuery.data) {
      invoicesQuery.fetch();
    }
  }, [lazyLoader.shouldLoad, invoicesQuery]);

  useEffect(() => {
    invoicesQuery.applyFilters({ invoiceStates: filters.invoiceStates });
  }, [filters.invoiceStates]);

  useEffect(() => {
    invoicesQuery.applyFilters({ grouped: filters.grouped });
  }, [filters.grouped]);

  useEffect(() => {
    invoicesQuery.applyFilters({ companyId });
  }, [companyId]);

  useEffect(() => {
    invoicesQuery.applyFilters({ customerAccountIds: filters.customerAccountIds });
  }, [filters.customerAccountIds]);

  useEffect(() => {
    invoicesQuery.applyFilters({ vendorAccountIds: filters.vendorAccountIds });
  }, [filters.vendorAccountIds]);

  useEffect(() => {
    invoicesQuery.applyFilters({
      equipmentOwnerCompanyIds: filters.equipmentOwnerCompanyIds,
    });
  }, [filters.equipmentOwnerCompanyIds]);

  useEffect(() => {
    invoicesQuery.applyFilters({ driverIds: filters.driverIds });
  }, [filters.driverIds]);

  useEffect(() => {
    invoicesQuery.applyFilters({ rateTypes: filters.rateTypes });
  }, [filters.rateTypes]);

  useEffect(() => {
    invoicesQuery.applyFilters({ pickupSiteIds: filters.pickupSiteIds });
  }, [filters.pickupSiteIds]);

  useEffect(() => {
    invoicesQuery.applyFilters({ dropoffSiteIds: filters.dropoffSiteIds });
  }, [filters.dropoffSiteIds]);

  const subscribeToInvoicesRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.InvoiceUpdateChannel,
      companyId,
      setInvoicesRTUSubscription,
      ({ data }: { data: Invoice_Read }) => {
        const parsedInvoice = Invoice.parse(data);
        const queryKey = makeInvoicesQueryContextKey(
          parsedInvoice.order?.id ?? '',
          currentCategory,
        );

        queryClient.setQueryData<Invoice[]>([queryKey], (cachedInvoices) => {
          if (cachedInvoices) {
            return map(cachedInvoices, (invoice) =>
              invoice.id === parsedInvoice.id ? parsedInvoice : invoice,
            );
          }

          return cachedInvoices;
        });
      },
    );
  }, [companyId, currentCategory]);

  useEffect(() => {
    if (!invoicesRTUSubscription) {
      subscribeToInvoicesRTU();
    }
    return () => {
      invoicesRTUSubscription?.unsubscribe();
    };
  }, [invoicesRTUSubscription, subscribeToInvoicesRTU]);

  if (!invoicesQuery.data) {
    return (
      <Box ref={lazyLoader.nodeRef}>
        <LoadingSpinner sx={{ my: 2 }} isVisible />
      </Box>
    );
  }

  return (
    <Box>
      <InvoicesTable invoices={invoicesQuery.data} />
    </Box>
  );
}
